import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ThemeProvider } from '@/context/ThemeContext';

const rutappLogo = "/images/rutapp-logo.png.png";

import { AppStateProvider } from '@/context/AppStateContext';
import { UserProvider } from '@/context/UserContext';
import { DateProvider } from '@/context/DateContext';
import { DogListProvider } from '@/context/DogListContext';
import { HistoryProvider } from '@/context/HistoryContext';
import { ConfigProvider } from '@/context/ConfigContext';
import { SubscriptionProvider } from '@/context/SubscriptionContext';
import { useAuth } from '@/context/AuthContext';
import { db } from '@/firebase';

// Valida que una membresía canónica (accountMemberships/{uid}) tenga la
// forma mínima requerida antes de confiar en ella para resolver la sesión.
// Fase 3B.3: solo lectura y precedencia. No crea, no migra, no retira
// todavía el flujo legado. Un documento existente pero inválido NUNCA debe
// tratarse como ausencia de membresía (no degradar al modelo legado).
function isValidAccountMembership(data) {
  if (!data) return false;
  if (typeof data.workspaceId !== 'string' || data.workspaceId.trim() === '') return false;
  if (data.role !== 'admin' && data.role !== 'worker') return false;
  // assignedProfileId es obligatorio solo para workers.
  // Un admin de su propio workspace (sin perfil asignado) es válido.
  if (data.role === 'worker' && (typeof data.assignedProfileId !== 'string' || data.assignedProfileId.trim() === '')) return false;
  return true;
}


export function AppProviders({ children }) {
  const { user } = useAuth();

  // bindingState: "resolving" | "resolved_admin" | "resolved_worker" | "error"
  const [bindingState, setBindingState] = useState('resolving');
  const [workspaceId, setWorkspaceId] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [assignedProfileIds, setAssignedProfileIds] = useState(null);

  // Ref para descartar respuestas asíncronas de un UID ya obsoleto
  // (evita que una lectura tardía de un usuario anterior pise el estado del actual)
  const requestUidRef = useRef(null);

  // Ref para invocar bootstrapPersonalAdmin como máximo una vez por UID
  // dentro de esta resolución de sesión. Evita loops/reintentos indefinidos
  // si, tras el bootstrap y la relectura, la membership sigue sin existir
  // o sigue siendo inválida.
  const bootstrapAttemptedRef = useRef(null);


  useEffect(() => {
    const uid = user?.uid;

    // Sin usuario autenticado: limpiar todo el estado derivado.
    if (!uid) {
      requestUidRef.current = null;
      setWorkspaceId(null);
      setUserRole(null);
      setAssignedProfileIds(null);

      // Conserva el modo bypass de desarrollo (BYPASS_LOGIN_TEMPORARILY en
      // authGate.jsx), que no pasa por Firebase Auth real y por tanto nunca
      // tendría user.uid. No implica lectura de membresías reales.
      if (import.meta.env.DEV) {
        setWorkspaceId('local');
        setUserRole('admin');
        setAssignedProfileIds(null);
        setBindingState('resolved_admin');
      } else {
        setBindingState('resolving');
      }
      return;
    }

    // Nuevo UID: reiniciar a "resolving" y descartar cualquier resolución previa
    requestUidRef.current = uid;
    bootstrapAttemptedRef.current = null;
    setBindingState('resolving');
    setWorkspaceId(null);
    setUserRole(null);
    setAssignedProfileIds(null);

    // Aplica la membership válida ya leída (por la lectura inicial o por la
    // relectura posterior al bootstrap) al estado de sesión.
    const applyMembership = (membershipData) => {
      setWorkspaceId(membershipData.workspaceId);
      setUserRole(membershipData.role);
      setAssignedProfileIds([membershipData.assignedProfileId]);
      setBindingState(
        membershipData.role === 'admin' ? 'resolved_admin' : 'resolved_worker'
      );
    };

    const resolveBinding = async () => {
      try {
        // Fase 3B.5A: accountMemberships/{uid} es la única fuente de
        // resolución. Si el documento NO existe, se bloquea el arranque.
        // Un documento existente pero inválido también bloquea (error de sesión).
        const membershipRef = doc(db, 'accountMemberships', uid);
        const membershipSnap = await getDoc(membershipRef);

        // Descartar si el usuario ya cambió mientras se resolvía la lectura
        if (requestUidRef.current !== uid) return;

        if (membershipSnap.exists()) {
          const membershipData = membershipSnap.data();
          if (!isValidAccountMembership(membershipData)) {
            // Membresía existente pero inválida: bloquear arranque.
            // Nunca degradar silenciosamente al modelo legado.
            console.error('[AppProviders] accountMembership inválida para uid:', uid);
            setBindingState('error');
            return;
          }

          // Membresía válida: resuelve exclusivamente desde accountMemberships.
          applyMembership(membershipData);
          return;
        }

        // accountMemberships inexistente: intentar el bootstrap del
        // workspace personal, como máximo una vez por UID en esta
        // resolución de sesión. No se asume desde el cliente que el
        // resultado del bootstrap sea role: 'admin': tras invocarlo,
        // Firestore/accountMemberships vuelve a ser la única fuente de
        // verdad mediante una segunda lectura.
        if (bootstrapAttemptedRef.current === uid) {
          console.error('[AppProviders] accountMembership sigue inexistente tras bootstrap para uid:', uid);
          setBindingState('error');
          return;
        }
        bootstrapAttemptedRef.current = uid;

        try {
          const functionsInstance = getFunctions();
          const bootstrapPersonalAdmin = httpsCallable(functionsInstance, 'bootstrapPersonalAdmin');
          await bootstrapPersonalAdmin();
        } catch (bootstrapError) {
          if (requestUidRef.current !== uid) return;
          // No bloquear aquí: el error de la callable no descarta que la
          // membership exista realmente (el backend pudo crearla antes de
          // que la respuesta se perdiera, o una vinculación externa
          // concurrente ya la haya creado). Se registra el error y se
          // continúa igualmente hacia la segunda lectura de
          // accountMemberships ya existente inmediatamente después.
          console.error('[AppProviders] Error en bootstrapPersonalAdmin:', bootstrapError);
        }


        // Descartar si el usuario ya cambió mientras se ejecutaba el bootstrap
        if (requestUidRef.current !== uid) return;

        // Segunda lectura: la membership real tras el bootstrap puede ser
        // la Admin personal recién creada, o una membership externa creada
        // concurrentemente por inviteWorkerWithProfile. Se resuelve
        // exclusivamente con lo que exista realmente en Firestore.
        const secondMembershipSnap = await getDoc(membershipRef);

        if (requestUidRef.current !== uid) return;

        if (secondMembershipSnap.exists()) {
          const secondMembershipData = secondMembershipSnap.data();
          if (!isValidAccountMembership(secondMembershipData)) {
            console.error('[AppProviders] accountMembership inválida tras bootstrap para uid:', uid);
            setBindingState('error');
            return;
          }

          applyMembership(secondMembershipData);
          return;
        }

        // Tras el bootstrap sigue sin existir: error controlado, sin loops.
        console.error('[AppProviders] accountMembership inexistente tras bootstrap para uid:', uid);
        setBindingState('error');
      } catch (error) {
        // Descartar si el usuario ya cambió mientras se resolvía la lectura
        if (requestUidRef.current !== uid) return;
        console.error('[AppProviders] Error resolviendo la sesión:', error);
        // Fallo de lectura: bloquear arranque.
        setBindingState('error');
      }
    };


    resolveBinding();
  }, [user?.uid]);


  // Mientras se resuelve el binding: no montar ConfigProvider ni consumidores
  // del workspace. Solo estado de carga.
  if (bindingState === 'resolving') {
    return (
      <div className="w-full h-screen flex flex-col items-center justify-center gap-4 bg-black">
        <img
          src={rutappLogo}
          alt="Rutapp"
          className="h-28 w-auto opacity-90"
        />
        <p style={{color: '#B0B0B0'}}>Cargando…</p>
      </div>
    );
  }

  // Binding inválido o error de lectura: bloquear arranque explícitamente.
  if (bindingState === 'error') {
    return <div>No se pudo verificar el acceso. Contacta con el administrador.</div>;
  }

  return (
    <ThemeProvider>
      <SubscriptionProvider workspaceId={workspaceId}>
        <ConfigProvider
          workspaceId={workspaceId}
          userRole={userRole}
          assignedProfileIds={assignedProfileIds}
        >
          <AppStateProvider>
            <UserProvider>
              <DateProvider>
                <DogListProvider>
                  <HistoryProvider>
                    {children}
                  </HistoryProvider>
                </DogListProvider>
              </DateProvider>
            </UserProvider>
          </AppStateProvider>
        </ConfigProvider>
      </SubscriptionProvider>
    </ThemeProvider>
  );
}
